import React, { useState } from 'react';

const ChatHistory = ({ chatData }) => {
  const [expandedSummaryId, setExpandedSummaryId] = useState(null);

  // Function to toggle summary expansion
  const toggleSummaryExpansion = (id) => {
    setExpandedSummaryId(prevId => prevId === id ? null : id);
  };

  // Function to group chat data by date
  const groupChatsByDate = (chats) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    
    const grouped = {};
    
    chats.forEach(chat => {
      const chatDate = new Date(`${chat.date} ${chat.time}`);
      let groupKey;
      
      if (chatDate >= today) {
        groupKey = 'Today';
      } else if (chatDate >= yesterday) {
        groupKey = 'Yesterday';
      } else if (chatDate >= lastWeek) {
        groupKey = 'Last Week';
      } else {
        groupKey = chat.date; // Use the original date string for older entries
      }
      
      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }
      
      // Add a unique ID to each chat entry
      grouped[groupKey].push({
        ...chat,
        id: `${groupKey}-${chat.date}-${chat.time}-${chat.symptoms}` // Create a unique ID
      });
    });
    
    return grouped;
  };

  // Function to properly format and capitalize symptoms
  const formatSymptoms = (symptoms) => {
    try {
      let symptomText = symptoms;
      
      // If symptoms is a stringified JSON
      if (typeof symptoms === 'string' && symptoms.startsWith('{')) {
        const parsed = JSON.parse(symptoms);
        symptomText = parsed.symptoms || symptoms;
      }
      
      // Convert to lowercase first
      symptomText = symptomText.toLowerCase();
      
      // Handle special cases like "nosebleed" becoming "nose bleed"
      symptomText = symptomText
        .replace(/([a-z])([A-Z])/g, '$1 $2') // Split camelCase
        .replace(/([a-z])([0-9])/g, '$1 $2') // Split letters and numbers
        .replace(/_/g, ' ') // Replace underscores with spaces
        .replace(/-/g, ' ') // Replace hyphens with spaces
        .replace(/\s+/g, ' ') // Collapse multiple spaces
        .trim();
      
      return symptomText;
    } catch (e) {
      return symptoms.toLowerCase();
    }
  };

  // Function to calculate time ago
  const getTimeAgo = (dateStr, timeStr) => {
    const chatTime = new Date(`${dateStr} ${timeStr}`);
    const now = new Date();
    const diffInSeconds = Math.floor((now - chatTime) / 1000);
    
    if (diffInSeconds < 60) {
      return 'just now';
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `last consulted ${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `last consulted ${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `last consulted ${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  };

  // Properly capitalize each word in the string
  const capitalize = (str) => {
    if (!str) return '';
    
    // List of words to keep lowercase unless they're the first word
    const lowercaseWords = ['and', 'or', 'the', 'a', 'an', 'but', 'for', 'at', 'by', 'to', 'from'];
    
    return str.split(' ').map((word, index) => {
      // Keep certain words lowercase unless they're the first word
      if (index !== 0 && lowercaseWords.includes(word.toLowerCase())) {
        return word.toLowerCase();
      }
      
      // Handle words with apostrophes (e.g., "I'm")
      const apostropheIndex = word.indexOf("'");
      if (apostropheIndex > 0) {
        return word.charAt(0).toUpperCase() + 
               word.slice(1, apostropheIndex + 1).toLowerCase() + 
               word.slice(apostropheIndex + 1).toLowerCase();
      }
      
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }).join(' ');
  };

  // Group the chat data
  const groupedChats = groupChatsByDate(chatData);

  return (
    <div className='flex flex-col gap-4 w-full instrument-sans-medium p-4 bg-white max-w-6xl rounded-xl border border-[#E9EBE9]'>
      {Object.keys(groupedChats).length > 0 ? (
        <div className="overflow-x-auto w-full">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr className="w-full">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider w-1/3">
                  Issue
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider w-2/3">
                  Summary
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-600">
              {Object.entries(groupedChats).map(([dateGroup, chats]) => (
                <React.Fragment key={dateGroup}>
                  <tr className="">
                    <td colSpan="2" className="px-6 py-2 text-sm font-medium text-[#828282]">
                      {dateGroup}
                    </td>
                  </tr>
                  {chats.map((entry) => (
                    <tr key={entry.id}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-black">
                          {capitalize(formatSymptoms(entry.symptoms))}
                        </div>
                        <div className="text-xs text-gray-500">
                          {getTimeAgo(entry.date, entry.time)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-black">
                          {expandedSummaryId === entry.id 
                            ? entry.response 
                            : `${entry.response.substring(0, 100)}${entry.response.length > 100 ? '...' : ''}`
                          }
                        </div>
                        {entry.response.length > 100 && (
                          <button 
                            onClick={() => toggleSummaryExpansion(entry.id)}
                            className="text-xs text-blue-500 hover:text-blue-700 mt-1 focus:outline-none"
                          >
                            {expandedSummaryId === entry.id ? 'See Less' : 'See More'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="px-6 py-4 text-center text-sm text-gray-500">
          No chat history available
        </div>
      )}
    </div>
  );
};

export default ChatHistory;